import React from 'react';
import { Department } from '@/pages/Departments';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

interface DepartmentsListProps {
    departments: Department[];
    onEdit: (department: Department) => void;
    onDelete: (departmentId: string) => void;
}

const DepartmentsList: React.FC<DepartmentsListProps> = ({ departments, onEdit, onDelete }) => {

    const buildHierarchy = (items: Department[], parentId: string | null = null): Department[] => {
        return items
            .filter(item => item.parentId === parentId)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(item => ({
                ...item,
                children: buildHierarchy(items, item.id)
            }));
    };

    const departmentHierarchy = buildHierarchy(departments);

    const renderDepartment = (dept: Department, level = 0) => {
        const { children, ...deptToEdit } = dept;
        return (
            <React.Fragment key={dept.id}>
                <tr className={level > 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ paddingLeft: `${1.5 + level * 1.5}rem` }}>
                        {dept.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dept.responsibleName || <span className="text-xs text-gray-400">N/A</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dept.completeDescription.includes(' < ') ? dept.completeDescription.substring(0, dept.completeDescription.lastIndexOf(' < ')) : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => onEdit(deptToEdit)} className="text-indigo-600 hover:text-indigo-900 mr-3">
                            <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button onClick={() => onDelete(dept.id)} className="text-red-600 hover:text-red-900">
                            <TrashIcon className="h-5 w-5" />
                        </button>
                    </td>
                </tr>
                {children?.map(child => renderDepartment(child, level + 1))}
            </React.Fragment>
        );
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nome
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Responsável
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Descrição Completa
                        </th>
                        <th scope="col" className="relative px-6 py-3">
                            <span className="sr-only">Ações</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {departmentHierarchy.map(dept => renderDepartment(dept))}
                </tbody>
            </table>
        </div>
    );
};

export default DepartmentsList;
